import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import Link from 'next/link'
import classes from '@src/styles/vs-competition.module.css'
import TrustBox from '@src/pages/common/components/TrustBox'
import { segmentTrack } from '@src/functions/segment'
import useAppLink from '@src/hooks/useAppLink'

export type HeroProps = {
  headingPrefix: string
  competitors: string[]
  /**
   * A competitor named by the ad the visitor clicked (`?competitor=`). When set, the
   * heading shows only this name and does not rotate.
   */
  pinned?: string | null
  lede: string
  ctaText: string
  ctaLink: string
  statsHeading: string
  stats: { value: string; label: string; isWord?: boolean }[]
  /**
   * Opens the quote modal. The CTA stays a real link to the booking app so that
   * crawlers and anyone without JS still get a working path to a quote; the handler
   * just intercepts the click for everyone else.
   */
  onQuoteClick?: () => void
}

/**
 * Cycles the competitor name in the H1 so a visitor arriving from any of the ten
 * branded queries sees their own vendor named.
 *
 * The first name is rendered server-side and never removed from the DOM, so the
 * H1 is complete for crawlers and for anyone with JS off or reduced motion on —
 * the rotation is decoration on top of a finished heading, not the heading.
 *
 * A pinned competitor (from a competitor-specific ad) replaces the rotation outright.
 */
function useCompetitorCycle(competitors: string[], pinned?: string | null) {
  const [index, setIndex] = useState(0)
  const [fading, setFading] = useState(false)
  const fadeTimer = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    if (pinned || competitors.length < 2) return undefined
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined

    const tick = setInterval(() => {
      setFading(true)
      clearTimeout(fadeTimer.current)
      fadeTimer.current = setTimeout(() => {
        setIndex((i) => (i + 1) % competitors.length)
        setFading(false)
      }, 280)
    }, 2600)

    return () => {
      clearInterval(tick)
      clearTimeout(fadeTimer.current)
    }
  }, [competitors, pinned])

  if (pinned) return { name: pinned, fading: false }
  return { name: competitors[index], fading }
}

function Hero({
  headingPrefix,
  competitors,
  pinned,
  lede,
  ctaText,
  ctaLink,
  statsHeading,
  stats,
  onQuoteClick,
}: HeroProps) {
  const { name, fading } = useCompetitorCycle(competitors, pinned)
  const appLink = useAppLink()

  return (
    <section className={classes.hero}>
      <div className={classes.heroGrid}>
        <div>
          {/*
            Two fixed lines, always. The lead word is its own block and the
            competitor line never wraps, so swapping a short name (Veolia) for
            the longest (Clean Management) cannot change the heading's height
            and shove the rest of the page down.
          */}
          <h1 className={classes.h1}>
            {/* Trailing space keeps the accessible name "Leaving <competitor>?" — the
                two spans are separate lines visually, so it collapses on screen. */}
            <span className={classes.h1Lead}>{headingPrefix} </span>
            <span className={classes.h1Name}>
              <span className={classNames(classes.swap, { [classes.swapOut]: fading })}>
                {name}
              </span>
              ?
            </span>
          </h1>
          <p className={classes.lede}>{lede}</p>
          <div className={classes.heroCta}>
            <Link
              className={classes.heroQuoteButton}
              href={appLink(ctaLink)}
              onClick={(event) => {
                segmentTrack('Click', { button: ctaText, page: 'Vs Competition' })
                if (!onQuoteClick) return
                event.preventDefault()
                onQuoteClick()
              }}
            >
              {ctaText}
            </Link>
            <div className={classes.heroTrust}>
              <TrustBox isMobile isDark={false} />
            </div>
          </div>
        </div>
        <aside className={classes.statCard}>
          <div className={classes.statCardHead}>
            <p className={classes.eyebrow}>{statsHeading}</p>
          </div>
          {stats.map((stat) => (
            <div key={stat.label} className={classes.stat}>
              <b
                className={classNames(
                  stat.isWord ? classes.statValueWord : classes.statValue,
                  classes.tnum,
                )}
              >
                {stat.value}
              </b>
              <span className={classes.statLabel}>{stat.label}</span>
            </div>
          ))}
        </aside>
      </div>
    </section>
  )
}

export default Hero
